/**
 * Tutor Card Component
 * Displays tutor information in a card layout
 */

import React from 'react';
import { GraduationCap, Star, MapPin, BookOpen, Trophy } from 'lucide-react';
import MessageButton from '../../../features/messaging/components/MessageButton';

const TutorCard = ({ tutor, onClick }) => {
  // Calculate average grade
  const getAverageGrade = () => {
    if (!tutor.courses || tutor.courses.length === 0) return 0;
    
    const totalMarks = tutor.courses.reduce((sum, course) => sum + (course.finalMark || 0), 0);
    return Math.round(totalMarks / tutor.courses.length);
  };

  // Get grade color
  const getGradeColor = (grade) => {
    if (grade >= 90) return 'text-green-600';
    if (grade >= 80) return 'text-blue-600';
    if (grade >= 70) return 'text-yellow-600';
    if (grade >= 60) return 'text-orange-600';
    return 'text-red-600';
  };

  // Format course codes for display
  const getCourseCodes = () => {
    if (!tutor.courses || tutor.courses.length === 0) return [];
    return tutor.courses.map(course => course.courseCode).filter(Boolean);
  };

  const averageGrade = getAverageGrade();
  const courseCodes = getCourseCodes();

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-shadow cursor-pointer">
      {/* Card Header */}
      <div className="p-4 border-b border-gray-100">
        <div className="flex items-center space-x-3">
          <div className="w-12 h-12 rounded-full overflow-hidden flex-shrink-0">
            {tutor.photos && tutor.photos.length > 0 ? (
              <img 
                src={tutor.photos[0]} 
                alt={tutor.name} 
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center bg-green-100 text-green-600 font-medium">
                {tutor.name?.charAt(0) || 'T'}
              </div>
            )}
          </div>
          
          <div className="flex-1 min-w-0">
            <h3 className="text-lg font-medium text-gray-800 truncate">
              {tutor.name}
            </h3>
            <div className="flex items-center text-sm text-gray-500">
              <MapPin size={14} className="mr-1" />
              <span className="truncate">{tutor.university}</span>
            </div>
          </div>
          
          {/* Average Grade Badge */}
          {averageGrade > 0 && (
            <div className="flex items-center bg-gray-50 px-2 py-1 rounded-md">
              <Trophy size={14} className={`mr-1 ${getGradeColor(averageGrade)}`} />
              <span className={`text-sm font-medium ${getGradeColor(averageGrade)}`}>
                {averageGrade}%
              </span>
            </div>
          )}
        </div>
      </div>
      
      {/* Card Body */}
      <div className="p-4" onClick={onClick}>
        {/* Courses */}
        <div className="mb-4">
          <div className="flex items-center mb-2">
            <BookOpen size={16} className="text-gray-500 mr-2" />
            <span className="text-sm font-medium text-gray-700">
              Courses ({courseCodes.length})
            </span>
          </div>
          
          {courseCodes.length > 0 ? (
            <div className="flex flex-wrap gap-1">
              {courseCodes.slice(0, 4).map((courseCode, index) => (
                <span 
                  key={index}
                  className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-full"
                >
                  {courseCode}
                </span>
              ))}
              {courseCodes.length > 4 && (
                <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded-full">
                  +{courseCodes.length - 4} more
                </span>
              )}
            </div>
          ) : (
            <p className="text-sm text-gray-500">No courses listed</p>
          )}
        </div>
        
        {/* Course Grades Preview */}
        {tutor.courses && tutor.courses.length > 0 && (
          <div className="mb-4">
            <div className="flex items-center mb-2">
              <GraduationCap size={16} className="text-gray-500 mr-2" />
              <span className="text-sm font-medium text-gray-700">Top Grades</span>
            </div>
            
            <div className="space-y-1">
              {tutor.courses
                .sort((a, b) => (b.finalMark || 0) - (a.finalMark || 0))
                .slice(0, 3)
                .map((course, index) => (
                  <div key={index} className="flex justify-between items-center text-sm">
                    <span className="text-gray-600 truncate mr-2">
                      {course.courseCode}
                    </span>
                    <span className={`font-medium ${getGradeColor(course.finalMark || 0)}`}>
                      {course.finalMark || 0}%
                    </span>
                  </div>
                ))}
            </div>
          </div>
        )}
        
        {/* Verification Badge */}
        {tutor.requiresProof && (
          <div className="flex items-center text-sm text-green-600 mb-3">
            <Star size={14} className="mr-1" />
            <span>Verified Academic Records</span>
          </div>
        )}
      </div>
      
      {/* Card Footer */}
      <div className="px-4 py-3 border-t border-gray-100 bg-gray-50 flex justify-between items-center">
        <button
          onClick={onClick}
          className="text-sm text-blue-600 hover:text-blue-700 font-medium"
        >
          View Profile
        </button>
        
        <MessageButton
          userId={tutor.userId}
          variant="primary"
          className="px-3 py-1.5 text-sm rounded-md"
          showText={true}
        />
      </div>
    </div>
  );
};

export default TutorCard;